import { isEqual, sum } from 'lodash';
import { memo } from 'react';
import { parseStrokeDasharray } from '@/utils/file-kit';
import { Border, CellStyle, ITableLayer, ITableLayerProps } from '@/version-types';
import { getCellStyle } from '../utils/table';

type Path = { border: Border; direction: 'horizontal' | 'vertical'; pathD: string };

const getPathList = ({
    width,
    height,
    data: {
        allStyleId,
        cells: cellsProp,
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
        styles,
        presetId,
    },
}: Pick<ITableLayer, 'width' | 'height' | 'data'>): Path[] => {
    const firstCellStyle = [allStyleId, ...cellsProp[0][0]!.styleIds].reduce<CellStyle>(
        (res, styleId) => ({
            ...res,
            ...styles[styleId],
        }),
        {},
    );
    const tableFakeBorderTop = (firstCellStyle.border?.top?.width ?? 0) / 2;
    const tableFakeBorderLeft = (firstCellStyle.border?.left?.width ?? 0) / 2;
    const tableFakeBorderRight = width - tableFakeBorderLeft - sum(horizontalSizes);
    const tableFakeBorderBottom = height - tableFakeBorderTop - sum(verticalSizes);

    const lefts = [
        ...horizontalSizes.map((_, index) => horizontalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        sum(horizontalSizes),
    ];
    const tops = [
        ...verticalSizes.map((_, index) => verticalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        sum(verticalSizes),
    ];
    const pathList: Path[] = [];
    const appendPathItem = ({
        pathD,
        border,
        direction,
    }: {
        pathD: string;
        border: Border;
        direction: 'horizontal' | 'vertical';
    }) => {
        const pathItem = pathList.find(({ border: b, direction: d }) => isEqual(b, border) && d === direction);
        if (pathItem) {
            pathItem.pathD += ' ' + pathD;
        } else {
            pathList.push({
                pathD,
                border,
                direction,
            });
        }
    };
    cellsProp.forEach((trCells, vIndex) => {
        trCells.forEach((cell, hIndex) => {
            if (!cell) return;
            const style = getCellStyle(cell, { allStyleId, styles, presetId });
            if (!style.border) return;

            appendPathItem({
                pathD: getPathD({
                    direction: 'horizontal',
                    hIndex,
                    vIndex,
                    span: cell.colSpan,
                }),
                border: style.border.top,
                direction: 'horizontal',
            });
            appendPathItem({
                pathD: getPathD({ direction: 'vertical', hIndex, vIndex, span: cell.rowSpan }),
                border: style.border.left,
                direction: 'vertical',
            });
            if (vIndex + cell.rowSpan === verticalSizes.length) {
                appendPathItem({
                    pathD: getPathD({
                        direction: 'horizontal',
                        hIndex,
                        vIndex: vIndex + cell.rowSpan,
                        span: cell.colSpan,
                    }),
                    border: style.border.bottom,
                    direction: 'horizontal',
                });
            }
            if (hIndex + cell.colSpan === horizontalSizes.length) {
                appendPathItem({
                    pathD: getPathD({
                        direction: 'vertical',
                        hIndex: hIndex + cell.colSpan,
                        vIndex,
                        span: cell.rowSpan,
                    }),
                    border: style.border.right,
                    direction: 'vertical',
                });
            }
        });
    });
    // 先画横向的, 再画纵向的
    const sortedPathList = pathList.sort((a, b) => a.direction.charCodeAt(0) - b.direction.charCodeAt(0));
    return sortedPathList;

    function getPathD({
        direction,
        hIndex,
        vIndex,
        span,
    }: {
        direction: 'horizontal' | 'vertical';
        hIndex: number;
        vIndex: number;
        span: number;
    }): string {
        let startX = tableFakeBorderLeft + lefts[hIndex];
        if (hIndex === 0 && direction === 'horizontal') startX -= tableFakeBorderLeft;
        let startY = tableFakeBorderTop + tops[vIndex];
        if (vIndex === 0 && direction === 'vertical') startY -= tableFakeBorderTop;
        let endX = direction === 'horizontal' ? tableFakeBorderLeft + lefts[hIndex + span] : startX;
        if (hIndex === horizontalSizes.length - 1 && direction === 'horizontal') endX += tableFakeBorderRight;
        let endY = direction === 'vertical' ? tableFakeBorderTop + tops[vIndex + span] : startY;
        if (vIndex === verticalSizes.length - 1 && direction === 'vertical') endY += tableFakeBorderBottom;
        return `M${startX},${startY} L${endX},${endY}`;
    }
};

const Borders = memo(
    ({
        tableLayerData,
        tableWidth,
        tableHeight,
    }: {
        tableLayerData: ITableLayerProps;
        tableWidth: number;
        tableHeight: number;
    }) => {
        // console.time('===render Borders');
        const pathList = getPathList({
            width: tableWidth,
            height: tableHeight,
            data: tableLayerData,
        });
        // console.timeEnd('===render Borders');

        return (
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: tableWidth,
                    height: tableHeight,
                    zIndex: 1,
                }}
                viewBox={`0 0 ${tableWidth} ${tableHeight}`}
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
            >
                {pathList.map(({ border, pathD }, i) => (
                    <path
                        key={`path-${i}`}
                        fill="none"
                        d={pathD}
                        stroke={border.color}
                        strokeWidth={border.width}
                        strokeDasharray={
                            parseStrokeDasharray(
                                {
                                    'line-0': '',
                                    'line-1': '2,1',
                                    'line-2': '1,1',
                                    'line-3': '2.5,0.5,0.5,0.5',
                                    'line-4': '2.5,0.5,0.5,0.5,0.5,0.5',
                                }[border.style],
                                border.width * 2,
                            ) as string
                        }
                    />
                ))}
            </svg>
        );
    },
);

Borders.displayName = 'Borders';

export default Borders;
